import { useCallback, useEffect, useRef, useState } from "react";
import { listManufacturingRecipes, type MfgRecipeSummary } from "@/lib/manufacturingClient";
import type { MfgDealMaterialsPreviewResponse, MfgDealCogsSnapshotRow, MfgDealFullCogsPreview, MfgOpsDealRow } from "@/lib/mfgOrdersClient";
import {
  commitManufacturingDealCogsSnapshot,
  getManufacturingDealCogsSnapshot,
  getManufacturingDealFullCogsPreview,
  getManufacturingDealMaterialsPreview,
  getManufacturingDealRecipeLinks,
  listManufacturingOrdersDeals,
  setManufacturingDealRecipeLinks,
} from "@/lib/mfgOrdersClient";
import { listManufacturingMaterialIssuances } from "@/lib/manufacturing/materialIssuanceClient";

export type MfgOrderLinkEditRow = {
  rowId: string;
  recipe_id: string;
  quantity: string;
};

function saleHintFromMetadata(metadata: Record<string, unknown> | undefined): string {
  if (!metadata) return "";
  const raw = metadata.mfg_sale_price_usd ?? metadata.sale_price_usd;
  if (typeof raw === "number" && Number.isFinite(raw)) return String(raw);
  if (typeof raw === "string") {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? String(n) : "";
  }
  return "";
}

function saleOptsFromString(s: string): { sale_price_usd?: number } | undefined {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? { sale_price_usd: n } : undefined;
}

export function useManufacturingOrdersTab(ordersNavTick: number) {
  const [deals, setDeals] = useState<MfgOpsDealRow[]>([]);
  const [dealsBusy, setDealsBusy] = useState(false);
  const [recipes, setRecipes] = useState<MfgRecipeSummary[]>([]);
  const [recipesBusy, setRecipesBusy] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [linkRows, setLinkRows] = useState<MfgOrderLinkEditRow[]>([]);
  const [salePriceInput, setSalePriceInput] = useState("");
  const [preview, setPreview] = useState<MfgDealMaterialsPreviewResponse | null>(null);
  const [fullCogsPreview, setFullCogsPreview] = useState<MfgDealFullCogsPreview | null>(null);
  const [snapshot, setSnapshot] = useState<MfgDealCogsSnapshotRow | null>(null);
  const [materialsSnapshot, setMaterialsSnapshot] = useState<MfgDealCogsSnapshotRow | null>(null);
  const [buildSnapshot, setBuildSnapshot] = useState<MfgDealCogsSnapshotRow | null>(null);
  const [issuanceActualUsd, setIssuanceActualUsd] = useState<number | null>(null);
  const [commitBusy, setCommitBusy] = useState(false);
  const [detailBusy, setDetailBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const recipesRef = useRef<MfgRecipeSummary[]>([]);
  recipesRef.current = recipes;

  const saleInputRef = useRef("");
  saleInputRef.current = salePriceInput;

  const prevSelectedDealIdRef = useRef<string | null>(null);

  const defaultRecipeId = recipes[0]?.id ?? "";

  const loadCatalog = useCallback(async () => {
    setDealsBusy(true);
    setRecipesBusy(true);
    setLocalError(null);
    const [d, r] = await Promise.all([listManufacturingOrdersDeals(), listManufacturingRecipes()]);
    setDealsBusy(false);
    setRecipesBusy(false);
    if (!d.ok) {
      setDeals([]);
      setLocalError(d.error);
      return;
    }
    setDeals(Array.isArray(d.data.deals) ? d.data.deals : []);
    if (!r.ok) {
      setRecipes([]);
      setLocalError((prev) => prev ?? r.error);
      return;
    }
    setRecipes(Array.isArray(r.data.recipes) ? r.data.recipes : []);
  }, []);

  useEffect(() => {
    void loadCatalog();
  }, [ordersNavTick, loadCatalog]);

  useEffect(() => {
    if (dealsBusy) return;
    setSelectedDealId((prev) => {
      if (prev && deals.some((x) => x.id === prev)) return prev;
      return deals[0]?.id ?? null;
    });
  }, [deals, dealsBusy]);

  const runPreview = useCallback(async (dealId: string, saleString?: string) => {
    const src = saleString !== undefined ? saleString : saleInputRef.current;
    const saleOpts = saleOptsFromString(src);
    const [p, full] = await Promise.all([
      getManufacturingDealMaterialsPreview(dealId, saleOpts),
      getManufacturingDealFullCogsPreview(dealId, saleOpts),
    ]);
    if (!p.ok) {
      setPreview(null);
      setFullCogsPreview(null);
      setLocalError(p.error);
      return;
    }
    setPreview(p.data);
    setFullCogsPreview(full.ok ? full.data.preview : null);
    if (!full.ok) setLocalError((prev) => prev ?? full.error);
    else setLocalError(null);
  }, []);

  useEffect(() => {
    if (!selectedDealId) {
      prevSelectedDealIdRef.current = null;
      setLinkRows([]);
      setPreview(null);
      setFullCogsPreview(null);
      setSnapshot(null);
      setMaterialsSnapshot(null);
      setBuildSnapshot(null);
      setIssuanceActualUsd(null);
      setSalePriceInput("");
      return;
    }

    const dealChanged = prevSelectedDealIdRef.current !== selectedDealId;
    if (dealChanged) prevSelectedDealIdRef.current = selectedDealId;

    let cancelled = false;
    void (async () => {
      setDetailBusy(true);
      setLocalError(null);
      const deal = deals.find((d) => d.id === selectedDealId);
      const hint = saleHintFromMetadata(deal?.metadata);
      if (dealChanged) {
        setSalePriceInput(hint);
        saleInputRef.current = hint;
      }

      const lr = await getManufacturingDealRecipeLinks(selectedDealId);
      if (cancelled) return;
      if (!lr.ok) {
        setLinkRows([]);
        setPreview(null);
        setLocalError(lr.error);
        setDetailBusy(false);
        return;
      }

      const rlist = recipesRef.current;
      const rid = rlist[0]?.id ?? "";
      setLinkRows(
        lr.data.links.length
          ? lr.data.links.map((l) => ({
              rowId: l.id,
              recipe_id: l.recipe_id,
              quantity: String(l.recipe_quantity),
            }))
          : rid
            ? [{ rowId: crypto.randomUUID(), recipe_id: rid, quantity: "1" }]
            : []
      );

      const saleForPreview = dealChanged ? hint : saleInputRef.current;
      const [saleSnap, buildSnap, issRes] = await Promise.all([
        getManufacturingDealCogsSnapshot(selectedDealId, "sale_commit"),
        getManufacturingDealCogsSnapshot(selectedDealId, "build_commit"),
        listManufacturingMaterialIssuances({ deal_id: selectedDealId }),
      ]);
      if (!cancelled && saleSnap.ok) {
        setSnapshot(saleSnap.data.snapshot);
        setMaterialsSnapshot(saleSnap.data.materials_snapshot);
      }
      if (!cancelled && buildSnap.ok) {
        setBuildSnapshot(buildSnap.data.snapshot);
      }
      if (!cancelled && issRes.ok) {
        const total = issRes.data.events.reduce((s, e) => s + (e.extended_cost_usd || 0), 0);
        setIssuanceActualUsd(total);
      } else if (!cancelled) {
        setIssuanceActualUsd(null);
      }
      await runPreview(selectedDealId, saleForPreview);
      if (!cancelled) setDetailBusy(false);
    })();

    return () => {
      cancelled = true;
      setDetailBusy(false);
    };
  }, [selectedDealId, ordersNavTick, deals, runPreview, recipes]);

  useEffect(() => {
    if (!selectedDealId || detailBusy) return;
    const t = window.setTimeout(() => {
      void runPreview(selectedDealId);
    }, 400);
    return () => window.clearTimeout(t);
  }, [salePriceInput, selectedDealId, detailBusy, runPreview]);

  function addLinkRow() {
    const rid = defaultRecipeId;
    if (!rid) return;
    setLinkRows((prev) => [...prev, { rowId: crypto.randomUUID(), recipe_id: rid, quantity: "1" }]);
  }

  function removeLinkRow(rowId: string) {
    setLinkRows((prev) => prev.filter((r) => r.rowId !== rowId));
  }

  function updateLinkRow(rowId: string, patch: Partial<Pick<MfgOrderLinkEditRow, "recipe_id" | "quantity">>) {
    setLinkRows((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...patch } : r)));
  }

  async function saveLinks() {
    if (!selectedDealId) return;
    setSaveBusy(true);
    setLocalError(null);
    const links: Array<{ recipe_id: string; recipe_quantity: number; notes?: string }> = [];
    let i = 0;
    for (const row of linkRows) {
      const recipe_id = row.recipe_id.trim();
      const recipe_quantity = Number(row.quantity);
      if (!recipe_id) {
        setLocalError(`Line ${i + 1}: pick a model (recipe).`);
        setSaveBusy(false);
        return;
      }
      if (!Number.isFinite(recipe_quantity) || recipe_quantity <= 0) {
        setLocalError(`Line ${i + 1}: quantity must be a positive number.`);
        setSaveBusy(false);
        return;
      }
      links.push({ recipe_id, recipe_quantity });
      i++;
    }
    const r = await setManufacturingDealRecipeLinks({ deal_id: selectedDealId, links });
    if (!r.ok) {
      setLocalError(r.error);
      setSaveBusy(false);
      return;
    }
    const lr = await getManufacturingDealRecipeLinks(selectedDealId);
    if (lr.ok) {
      const rlist = recipesRef.current;
      const rid = rlist[0]?.id ?? "";
      setLinkRows(
        lr.data.links.length
          ? lr.data.links.map((l) => ({
              rowId: l.id,
              recipe_id: l.recipe_id,
              quantity: String(l.recipe_quantity),
            }))
          : rid
            ? [{ rowId: crypto.randomUUID(), recipe_id: rid, quantity: "1" }]
            : []
      );
    }
    await runPreview(selectedDealId);
    setSaveBusy(false);
  }

  async function commitSnapshot(
    kind: "materials_commit" | "build_commit" | "sale_commit" = "sale_commit",
  ) {
    if (!selectedDealId) return;
    setCommitBusy(true);
    setLocalError(null);
    const saleParsed = saleOptsFromString(saleInputRef.current);
    const r = await commitManufacturingDealCogsSnapshot(selectedDealId, {
      snapshot_kind: kind,
      sale_price_usd: kind === "sale_commit" ? saleParsed?.sale_price_usd : undefined,
    });
    setCommitBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    if (kind === "sale_commit") setSnapshot(r.data.snapshot);
    if (kind === "materials_commit") setMaterialsSnapshot(r.data.snapshot);
    if (kind === "build_commit") setBuildSnapshot(r.data.snapshot);
    const [saleSnap, buildSnap] = await Promise.all([
      getManufacturingDealCogsSnapshot(selectedDealId, "sale_commit"),
      getManufacturingDealCogsSnapshot(selectedDealId, "build_commit"),
    ]);
    if (saleSnap.ok) {
      setSnapshot(saleSnap.data.snapshot);
      setMaterialsSnapshot(saleSnap.data.materials_snapshot);
    }
    if (buildSnap.ok) setBuildSnapshot(buildSnap.data.snapshot);
  }

  return {
    deals,
    dealsBusy,
    recipes,
    recipesBusy,
    selectedDealId,
    setSelectedDealId,
    linkRows,
    addLinkRow,
    removeLinkRow,
    updateLinkRow,
    salePriceInput,
    setSalePriceInput,
    preview,
    fullCogsPreview,
    snapshot,
    materialsSnapshot,
    buildSnapshot,
    issuanceActualUsd,
    detailBusy,
    saveBusy,
    commitBusy,
    localError,
    runPreviewNow: selectedDealId ? () => void runPreview(selectedDealId) : null,
    saveLinks,
    commitSnapshot: selectedDealId
      ? (kind?: "materials_commit" | "build_commit" | "sale_commit") =>
          void commitSnapshot(kind)
      : null,
    defaultRecipeId,
  };
}
